import { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { FlowArrow, Plus, Trash, PaperPlaneTilt, Play, Pause, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";

const QUILL_MODULES = { toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }], ["link"], ["clean"]] };

const STATUS_STYLES = {
  draft: "border-zinc-200 bg-zinc-50 text-zinc-600",
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paused: "border-amber-200 bg-amber-50 text-amber-700",
};

export default function SequencesPage() {
  const [items, setItems] = useState(null);
  const [pubs, setPubs] = useState([]);
  const [editing, setEditing] = useState(null); // sequence being edited (working copy)
  const [busy, setBusy] = useState(false);

  const load = () => api.listSequences().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); api.listPublications().then(d => setPubs(Array.isArray(d) ? d : (d?.items || []))).catch(() => {}); }, []);

  const newSequence = async () => {
    const doc = await api.createSequence({ name: "New nurture sequence", publication_id: null, steps: [{ delay_days: 3, subject: "", body_html: "<p>Hi {{first_name}},</p><p></p>" }] });
    setItems([doc, ...(items || [])]);
    setEditing(doc);
  };

  const saveEditing = async () => {
    setBusy(true);
    try {
      await api.updateSequence(editing.id, { name: editing.name, publication_id: editing.publication_id, steps: editing.steps });
      toast.success("Sequence saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const patchStep = (i, patch) => {
    const steps = editing.steps.map((st, j) => j === i ? { ...st, ...patch } : st);
    setEditing({ ...editing, steps });
  };

  return (
    <div className="space-y-5" data-testid="sequences-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-xl font-semibold flex items-center gap-2"><FlowArrow size={22} className="text-indigo-500" /> Nurture sequences</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Automatic follow-up emails for people who request a gated publication. Approved sequences run hands-free; unsubscribes stop them instantly.</p>
        </div>
        <button data-testid="new-sequence-btn" onClick={newSequence} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14} /> New sequence</button>
      </div>

      {items === null && <div className="text-sm text-zinc-400 py-10 text-center">Loading…</div>}
      {items?.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
          No sequences yet. Create one to automatically follow up with everyone who downloads a publication — e.g. a thank-you note after 3 days and a related-report suggestion after a week.
        </div>
      )}

      <div className="space-y-3">
        {(items || []).map(s => (
          <div key={s.id} className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`sequence-card-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{s.name}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[s.status] || STATUS_STYLES.draft}`}>{s.status}</span>
                  {s.approval?.approved_at && s.approval_valid && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle size={11} weight="fill" /> approved</span>}
                  {s.approval?.requested_at && !s.approval?.approved_at && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">awaiting approval</span>}
                  {s.approval?.approved_at && !s.approval_valid && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700"><Warning size={11} /> edited since approval</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Trigger: {s.publication_id ? `download of "${pubs.find(p => p.id === s.publication_id)?.title || "a publication"}"` : "any publication download"} · {s.steps.length} step{s.steps.length !== 1 ? "s" : ""}
                  {" · "}{s.stats?.enrollments?.active || 0} in progress · {s.stats?.enrollments?.completed || 0} completed
                </div>
                {s.stats?.steps?.some(st => st.sent > 0) && (
                  <div className="flex gap-3 mt-1.5 text-[11px] text-zinc-500 flex-wrap">
                    {s.stats.steps.map((st, i) => (
                      <span key={i}>Step {i + 1}: {st.sent} sent · {st.opened} opened · {st.clicked} clicked</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                {s.status !== "active" && <button data-testid={`sequence-edit-${s.id}`} onClick={() => setEditing(JSON.parse(JSON.stringify(s)))} className="text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Edit</button>}
                {s.status !== "active" && !s.approval_valid && <button data-testid={`sequence-approve-req-${s.id}`} onClick={() => action(() => api.sequenceRequestApproval(s.id), "Approval request emailed")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50"><PaperPlaneTilt size={12} /> Request approval</button>}
                {s.status !== "active" && s.approval_valid && <button data-testid={`sequence-activate-${s.id}`} onClick={() => action(() => api.sequenceActivate(s.id), "Sequence is live")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><Play size={12} weight="fill" /> Activate</button>}
                {s.status === "active" && <button data-testid={`sequence-pause-${s.id}`} onClick={() => action(() => api.sequencePause(s.id), "Sequence paused")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50"><Pause size={12} weight="fill" /> Pause</button>}
                {s.status !== "active" && <button data-testid={`sequence-delete-${s.id}`} onClick={() => { if (window.confirm("Delete this sequence and its enrolment history?")) action(() => api.deleteSequence(s.id), "Sequence deleted"); }} className="text-xs px-2 py-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50"><Trash size={12} /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 flex items-center justify-center p-4" data-testid="sequence-editor">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="font-heading text-base font-medium">Edit sequence</h3>
              <button onClick={() => setEditing(null)} className="text-sm text-zinc-500 hover:text-zinc-900">Close</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Name</span>
                  <input data-testid="sequence-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Trigger</span>
                  <select data-testid="sequence-trigger" value={editing.publication_id || ""} onChange={e => setEditing({ ...editing, publication_id: e.target.value || null })} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                    <option value="">Any publication download</option>
                    {pubs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </label>
              </div>

              {editing.steps.map((st, i) => (
                <div key={st.id || i} className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid={`sequence-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Step {i + 1}
                      <span className="text-xs font-normal text-zinc-500">sends</span>
                      <input data-testid={`step-delay-${i}`} type="number" min={0} max={90} value={st.delay_days} onChange={e => patchStep(i, { delay_days: Number(e.target.value) })} className="h-7 w-16 rounded border border-zinc-200 px-2 text-xs" />
                      <span className="text-xs font-normal text-zinc-500">day(s) after {i === 0 ? "the download" : `step ${i}`}</span>
                    </div>
                    {editing.steps.length > 1 && <button onClick={() => setEditing({ ...editing, steps: editing.steps.filter((_, j) => j !== i) })} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>}
                  </div>
                  <input data-testid={`step-subject-${i}`} value={st.subject} onChange={e => patchStep(i, { subject: e.target.value })} placeholder="Subject line (supports {{first_name}})" className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
                  <ReactQuill theme="snow" value={st.body_html} onChange={v => patchStep(i, { body_html: v })} modules={QUILL_MODULES} />
                </div>
              ))}
              <button data-testid="add-step-btn" onClick={() => setEditing({ ...editing, steps: [...editing.steps, { delay_days: 4, subject: "", body_html: "<p>Hi {{first_name}},</p><p></p>" }] })} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-dashed border-zinc-300 hover:border-zinc-500"><Plus size={12} /> Add step</button>
              <p className="text-[11px] text-zinc-500">Every send includes your standard unsubscribe footer and tracking. Unsubscribed or suppressed contacts are dropped from the sequence automatically. Editing after approval requires a fresh approval.</p>
            </div>
            <div className="px-5 py-4 border-t border-zinc-100 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50">Cancel</button>
              <button data-testid="sequence-save" onClick={saveEditing} disabled={busy} className="text-sm px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">{busy ? "Saving…" : "Save sequence"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
